import { CANONICAL_COLUMN_ORDER } from './ColumnOrder';
import { ExcelColumnConfig, LocalizedString } from '../../redux/application/application.types';
import { ResolvedExportConfig } from './ExportConfigResolver';
import { JobType } from '../../redux/jobs/jobs.types';
import { COLUMN_LABEL_KEYS, COLUMN_BAND_GROUPS } from './ColumnGroups';
import { hasExportColumnValue } from './ColumnValueResolver';
import { resolveLocalized } from '../../helper/LocalizedText';

export type ColumnZone = 'frozen' | 'scrollable' | 'hidden';

export interface ColumnSchema {
    key: string;            // dot-path or system key like '__jobId'
    zone: ColumnZone;       // frozen | scrollable | hidden
    locked: boolean;        // cell protection
    width?: number;         // column width in chars; undefined = auto
    label: string;          // resolved (localized) header label
    group?: string;         // band group key (see COLUMN_BAND_GROUPS); undefined for hidden
    validationType?: 'readingValue' | 'readingDate';   // which DataValidation to apply
    isFormula?: boolean;    // true = status formula cell
    colIndex?: number;      // 2-based (sheet column 1 is the medium color edge), set after schema is built
}

// Der Tarif gehört fachlich zur Zähler-Identität (Zählernummer + OBIS + Tarif)
// und wird deshalb aus den scrollbaren Zusatzspalten in die frozen zone direkt
// hinter die OBIS gezogen, sofern er im Format enthalten ist.
const TARIFF_COLUMN_KEY = 'meterRateTask.tariffName';

// Resolves a column header label for the given key. A per-format config label
// (LocalizedString) wins; otherwise the portal translation for the column's
// labelKey is used (which the backoffice can override via a language file).
export type ColumnLabelResolver = (key: string, configLabel?: LocalizedString) => string;

type TFunc = (key: string, opts?: { defaultValue?: string }) => string;

// Builds a ColumnLabelResolver from the i18next `t` function and the active
// language. Fallback chain: config label -> portal translation -> raw key
// (only as a last resort; every column is expected to have a translation).
export function createColumnLabelResolver(t: TFunc, language: string | undefined): ColumnLabelResolver {
    return (key, configLabel) => {
        const fromConfig = resolveLocalized(configLabel, language);
        if (fromConfig) {
            return fromConfig;
        }
        const labelKey = COLUMN_LABEL_KEYS[key];
        return labelKey ? t(labelKey, { defaultValue: key }) : key;
    };
}

export function buildSchema(
    jobs: JobType[],
    config: ResolvedExportConfig,
    resolveColumnLabel: ColumnLabelResolver,
): ColumnSchema[] {
    const schema: ColumnSchema[] = [];

    // Leere Zusatzspalten weglassen: Spalten, für die kein einziger (Job, Task)
    // einen Wert liefert, tragen nichts bei. Ohne Jobs (z.B. Vorschau/Tests)
    // ist keine Bewertung möglich — dann bleibt alles erhalten.
    const visibleAdditionalColumns = config.additionalColumns.filter(
        (col: ExcelColumnConfig) => col.visible && (jobs.length === 0 || hasExportColumnValue(col.key, jobs)),
    );

    const tariffConfig = visibleAdditionalColumns.find((col) => col.key === TARIFF_COLUMN_KEY);
    const nonTariffColumns = visibleAdditionalColumns.filter((col) => col.key !== TARIFF_COLUMN_KEY);

    // Zone 1: Frozen columns — Zähler-Identität, dann Eingabebereich.
    // __readingHint before __status.
    const frozenColumns: ColumnSchema[] = [
        {
            key: 'meter.meterNumber',
            zone: 'frozen',
            locked: true,
            width: 20,
            label: resolveColumnLabel('meter.meterNumber'),
            group: 'meter',
        },
        {
            key: 'meterRateTask.obis',
            zone: 'frozen',
            locked: true,
            width: 10,
            label: resolveColumnLabel('meterRateTask.obis'),
            group: 'meter',
        },
        ...(tariffConfig
            ? [{
                key: TARIFF_COLUMN_KEY,
                zone: 'frozen' as ColumnZone,
                locked: tariffConfig.locked ?? true,
                width: tariffConfig.width ?? 12,
                label: resolveColumnLabel(TARIFF_COLUMN_KEY, tariffConfig.label),
                group: 'meter',
            }]
            : []),
        {
            key: '__readingDate',
            zone: 'frozen',
            locked: false,
            width: 14,
            label: resolveColumnLabel('__readingDate'),
            group: 'reading',
            validationType: 'readingDate',
        },
        {
            key: '__readingValue',
            zone: 'frozen',
            locked: false,
            width: 14,
            label: resolveColumnLabel('__readingValue'),
            group: 'reading',
            validationType: 'readingValue',
        },
        {
            key: '__readingHint',
            zone: 'frozen',
            locked: false,
            width: 30,
            label: resolveColumnLabel('__readingHint'),
            group: 'reading',
        },
        {
            key: '__status',
            zone: 'frozen',
            locked: true,
            width: 14,
            label: resolveColumnLabel('__status'),
            group: 'reading',
            isFormula: true,
        },
    ];

    schema.push(...frozenColumns);

    // Frozen additional columns (config.additionalColumns with frozen: true) come after fixed frozen
    const frozenAdditionalColumns = nonTariffColumns
        .filter((col: ExcelColumnConfig) => col.frozen)
        .map((col: ExcelColumnConfig): ColumnSchema => ({
            key: col.key,
            zone: 'frozen',
            locked: col.locked ?? true,
            width: col.width,
            label: resolveColumnLabel(col.key, col.label),
            group: COLUMN_BAND_GROUPS[col.key],
        }));

    schema.push(...frozenAdditionalColumns);

    // Zone 2: Scrollable — targetReadingDate always injected, then additional
    // columns; alles gemeinsam in kanonischer Reihenfolge (Standort → Kunde →
    // Partner → Auftrag inkl. Zielablesedatum → Historie).
    const additionalBase: ExcelColumnConfig[] = [
        // targetReadingDate is always present, not user-selectable
        { key: 'meterRateTask.targetReadingDate', visible: true, locked: true, width: 16 },
        ...nonTariffColumns.filter((col: ExcelColumnConfig) => !col.frozen),
    ];

    const sorted = [...additionalBase].sort((a, b) => {
        const ai = CANONICAL_COLUMN_ORDER.indexOf(a.key);
        const bi = CANONICAL_COLUMN_ORDER.indexOf(b.key);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });

    const scrollableColumns = sorted.map((col: ExcelColumnConfig): ColumnSchema => ({
        key: col.key,
        zone: 'scrollable',
        locked: col.locked ?? true,
        width: col.width,
        label: resolveColumnLabel(col.key, col.label),
        group: COLUMN_BAND_GROUPS[col.key],
    }));

    schema.push(...scrollableColumns);

    // Zone 3: Hidden columns (system columns for formulas and import)
    const hiddenColumns: ColumnSchema[] = [
        { key: '__jobId', zone: 'hidden', locked: true, width: 8, label: 'Job-ID' },
        { key: '__taskId', zone: 'hidden', locked: true, width: 8, label: 'Task-ID' },
        { key: '__minValue', zone: 'hidden', locked: true, width: 8, label: 'Min-Wert' },
        { key: '__maxValue', zone: 'hidden', locked: true, width: 8, label: 'Max-Wert' },
        { key: '__postDecimals', zone: 'hidden', locked: true, width: 8, label: 'Nachkommastellen' },
        { key: '__preDecimals', zone: 'hidden', locked: true, width: 8, label: 'Vorkommastellen' },
        { key: '__strictValidation', zone: 'hidden', locked: true, width: 8, label: 'Strenge Validierung' },
        { key: '__noValidation', zone: 'hidden', locked: true, width: 8, label: 'Keine Validierung' },
        { key: '__decimalsCheck', zone: 'hidden', locked: true, width: 8, label: 'Nachkomma prüfen' },
        { key: '__histCheck', zone: 'hidden', locked: true, width: 8, label: 'Historic prüfen' },
        { key: '__contCheck', zone: 'hidden', locked: true, width: 8, label: 'Continuous prüfen' },
        { key: '__alreadyRead', zone: 'hidden', locked: true, width: 8, label: 'Bereits abgelesen' },
        { key: '__minAllowedDate', zone: 'hidden', locked: true, width: 8, label: 'Frühestes Ablesedatum' },
        { key: '__maxAllowedDate', zone: 'hidden', locked: true, width: 8, label: 'Spätestes Ablesedatum' },
    ];

    schema.push(...hiddenColumns);

    // Assign colIndex to each entry. Sheet column 1 is reserved for the medium
    // color edge, so schema columns start at sheet column 2.
    schema.forEach((col, index) => {
        col.colIndex = index + 2;
    });

    return schema;
}

export function getFreezeCount(schema: ColumnSchema[]): number {
    return schema.filter(col => col.zone === 'frozen').length;
}

export function getColIndex(schema: ColumnSchema[], key: string): number | undefined {
    return schema.find(col => col.key === key)?.colIndex;
}
